import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

const CHANNEL_NAME = 'rms-draft-editing-sync';
const HEARTBEAT_INTERVAL_MS = 30000; // 30 seconds
const STALE_THRESHOLD_MS = 60000; // 60 seconds
const TAKEOVER_TIMEOUT_MS = 3000; // 3 seconds timeout for takeover confirmation

export interface DraftEditingState {
  draftId: number;
  referenceId: string | number;
  tabId: string;
  timestamp: number;
}

export interface DraftDeletedPayload {
  draftId: number;
  referenceId: string | number;
  referenceType: string;
  tabId: string;
}

export type DraftSyncMessage =
  | {
      type:
        | 'DRAFT_EDITING_START'
        | 'DRAFT_EDITING_STOP'
        | 'HEARTBEAT'
        | 'REQUEST_STATE'
        | 'TAKEOVER_REQUEST'
        | 'TAKEOVER_CONFIRMED';
      payload: DraftEditingState;
    }
  | { type: 'DRAFT_DELETED'; payload: DraftDeletedPayload };

interface UseDraftEditingSyncOptions {
  onDraftDeleted: (payload: DraftDeletedPayload) => void;
  onTakeoverRequested: () => void;
}

interface UseDraftEditingSyncReturn {
  activeDrafts: Map<number, DraftEditingState>;
  startEditing: (draftId: number, referenceId: string | number) => void;
  stopEditing: () => void;
  isDraftActiveElsewhere: (draftId: number) => boolean;
  requestTakeover: (draftId: number) => Promise<boolean>;
  broadcastDraftDeleted: (draftId: number, referenceId: string | number, referenceType: string) => void;
}

const isBroadcastChannelSupported = (): boolean => {
  return typeof BroadcastChannel !== 'undefined';
};

const generateTabId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return `tab-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

export const useNoteDraftEditingSync = ({
  onTakeoverRequested,
  onDraftDeleted,
}: UseDraftEditingSyncOptions): UseDraftEditingSyncReturn => {
  const tabIdRef = useRef<string>(generateTabId());
  const channelRef = useRef<BroadcastChannel | null>(null);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cleanupIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentEditingRef = useRef<DraftEditingState | null>(null);
  const onTakeoverRequestedRef = useRef<() => void>(onTakeoverRequested);
  const takeoverResolversRef = useRef<Map<number, (confirmed: boolean) => void>>(new Map());
  const onDraftDeletedRef = useRef<(payload: DraftDeletedPayload) => void>(onDraftDeleted);

  useLayoutEffect(() => {
    onTakeoverRequestedRef.current = onTakeoverRequested;
    onDraftDeletedRef.current = onDraftDeleted;
  }, [onTakeoverRequested, onDraftDeleted]);

  const [activeDrafts, setActiveDrafts] = useState<Map<number, DraftEditingState>>(new Map());

  const broadcastMessage = useCallback((message: DraftSyncMessage) => {
    if (channelRef.current) {
      try {
        channelRef.current.postMessage(message);
      } catch {
        /* empty - channel may be closed */
      }
    }
  }, []);

  const cleanupStaleDrafts = useCallback(() => {
    const now = Date.now();
    setActiveDrafts((prev) => {
      const updated = new Map(prev);
      let hasChanges = false;

      updated.forEach((state, draftId) => {
        if (now - state.timestamp > STALE_THRESHOLD_MS) {
          updated.delete(draftId);
          hasChanges = true;
        }
      });

      return hasChanges ? updated : prev;
    });
  }, []);

  const handleMessage = useCallback(
    (event: MessageEvent<DraftSyncMessage>) => {
      const { type, payload } = event.data;

      if (payload.tabId === tabIdRef.current) {
        return;
      }

      switch (type) {
        case 'DRAFT_EDITING_START':
        case 'HEARTBEAT':
          setActiveDrafts((prev) => {
            const updated = new Map(prev);
            updated.set(payload.draftId, payload);
            return updated;
          });
          break;

        case 'DRAFT_EDITING_STOP':
          setActiveDrafts((prev) => {
            const existing = prev.get(payload.draftId);
            if (existing && existing.tabId === payload.tabId) {
              const updated = new Map(prev);
              updated.delete(payload.draftId);
              return updated;
            }
            return prev;
          });
          break;

        case 'REQUEST_STATE':
          if (currentEditingRef.current) {
            broadcastMessage({
              type: 'HEARTBEAT',
              payload: {
                ...currentEditingRef.current,
                timestamp: Date.now(),
              },
            });
          }
          break;

        case 'TAKEOVER_REQUEST':
          if (currentEditingRef.current && currentEditingRef.current.draftId === payload.draftId) {
            onTakeoverRequestedRef.current?.();
            currentEditingRef.current = null;
            broadcastMessage({
              type: 'TAKEOVER_CONFIRMED',
              payload: {
                ...payload,
                tabId: tabIdRef.current,
                timestamp: Date.now(),
              },
            });
          }
          break;

        case 'TAKEOVER_CONFIRMED': {
          const resolver = takeoverResolversRef.current.get(payload.draftId);
          if (resolver) {
            resolver(true);
            takeoverResolversRef.current.delete(payload.draftId);
          }
          setActiveDrafts((prev) => {
            const updated = new Map(prev);
            updated.delete(payload.draftId);
            return updated;
          });
          break;
        }

        case 'DRAFT_DELETED': {
          onDraftDeletedRef.current?.(payload as DraftDeletedPayload);
          break;
        }
      }
    },
    [broadcastMessage]
  );

  const startEditing = useCallback(
    (draftId: number, referenceId: string | number) => {
      if (currentEditingRef.current && currentEditingRef.current.draftId !== draftId) {
        broadcastMessage({
          type: 'DRAFT_EDITING_STOP',
          payload: { ...currentEditingRef.current, timestamp: Date.now() },
        });
      }

      const state: DraftEditingState = {
        draftId,
        referenceId,
        tabId: tabIdRef.current,
        timestamp: Date.now(),
      };

      currentEditingRef.current = state;

      broadcastMessage({
        type: 'DRAFT_EDITING_START',
        payload: state,
      });
    },
    [broadcastMessage]
  );

  const stopEditing = useCallback(() => {
    if (currentEditingRef.current) {
      broadcastMessage({
        type: 'DRAFT_EDITING_STOP',
        payload: {
          ...currentEditingRef.current,
          timestamp: Date.now(),
        },
      });
      currentEditingRef.current = null;
    }
  }, [broadcastMessage]);

  const isDraftActiveElsewhere = useCallback(
    (draftId: number): boolean => {
      const state = activeDrafts.get(draftId);
      if (!state) {
        return false;
      }

      const isStale = Date.now() - state.timestamp > STALE_THRESHOLD_MS;
      if (isStale) {
        return false;
      }

      return state.tabId !== tabIdRef.current;
    },
    [activeDrafts]
  );

  const requestTakeover = useCallback(
    (draftId: number): Promise<boolean> => {
      if (!isBroadcastChannelSupported() || !channelRef.current) {
        return Promise.resolve(true);
      }

      return new Promise((resolve) => {
        const timeoutId = setTimeout(() => {
          const resolver = takeoverResolversRef.current.get(draftId);
          if (resolver) {
            resolver(false);
            takeoverResolversRef.current.delete(draftId);
          }
        }, TAKEOVER_TIMEOUT_MS);

        const originalResolver = resolve;
        takeoverResolversRef.current.set(draftId, (confirmed: boolean) => {
          clearTimeout(timeoutId);
          originalResolver(confirmed);
        });

        broadcastMessage({
          type: 'TAKEOVER_REQUEST',
          payload: {
            draftId,
            referenceId: 0,
            tabId: tabIdRef.current,
            timestamp: Date.now(),
          },
        });
      });
    },
    [broadcastMessage]
  );

  const broadcastDraftDeleted = useCallback((draftId: number, referenceId: string | number, referenceType: string) => {
    if (channelRef.current) {
      try {
        channelRef.current.postMessage({
          type: 'DRAFT_DELETED',
          payload: { draftId, referenceId, referenceType, tabId: tabIdRef.current },
        } satisfies DraftSyncMessage);
      } catch {
        /* empty - channel may be closed */
      }
    }
  }, []);

  useEffect(() => {
    if (!isBroadcastChannelSupported()) {
      return;
    }

    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = handleMessage;

      broadcastMessage({
        type: 'REQUEST_STATE',
        payload: {
          draftId: 0,
          referenceId: 0,
          tabId: tabIdRef.current,
          timestamp: Date.now(),
        },
      });
    } catch {
      channelRef.current = null;
    }

    return () => {
      if (currentEditingRef.current && channelRef.current) {
        try {
          channelRef.current.postMessage({
            type: 'DRAFT_EDITING_STOP',
            payload: { ...currentEditingRef.current, timestamp: Date.now() },
          } satisfies DraftSyncMessage);
        } catch {
          /* channel may already be closed */
        }
        currentEditingRef.current = null;
      }
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [broadcastMessage, handleMessage]);

  useEffect(() => {
    if (!isBroadcastChannelSupported()) {
      return;
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (currentEditingRef.current) {
        const updatedState = {
          ...currentEditingRef.current,
          timestamp: Date.now(),
        };
        currentEditingRef.current = updatedState;

        broadcastMessage({
          type: 'HEARTBEAT',
          payload: updatedState,
        });
      }
    }, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    };
  }, [broadcastMessage]);

  useEffect(() => {
    if (!isBroadcastChannelSupported()) {
      return;
    }

    cleanupIntervalRef.current = setInterval(cleanupStaleDrafts, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (cleanupIntervalRef.current) {
        clearInterval(cleanupIntervalRef.current);
        cleanupIntervalRef.current = null;
      }
    };
  }, [cleanupStaleDrafts]);

  return {
    activeDrafts,
    startEditing,
    stopEditing,
    isDraftActiveElsewhere,
    requestTakeover,
    broadcastDraftDeleted,
  };
};
